/**
 * Product Preview
 *
 * Displays extracted product information.
 */

import { useState } from 'react';
import { Package, Tag, DollarSign, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/v2/ui';
import type { ExtractedProduct } from '@/lib/api/content-studio';

interface ProductPreviewProps {
  product: ExtractedProduct;
}

export function ProductPreview({ product }: ProductPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse description into list items if it contains multiple sentences/features
  const parseDescription = (desc: string) => {
    // Check if description contains bullet-point-like patterns
    const items = desc
      .split(/[.•\n]/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    // If we have multiple items (likely a list), return as array
    if (items.length > 1) {
      return items;
    }

    // Otherwise, return as single item
    return [desc];
  };

  const descriptionItems = product.description ? parseDescription(product.description) : [];
  const hasLongDescription = descriptionItems.length > 2 || (product.description?.length ?? 0) > 150;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="h-5 w-5" />
          Extrahiertes Produkt
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          {/* Product Image */}
          {product.image_url && (
            <div className="flex-shrink-0">
              <img
                src={product.image_url}
                alt={product.title}
                className="w-32 h-32 object-cover rounded-lg border"
              />
            </div>
          )}

          {/* Product Info */}
          <div className="flex-1 space-y-3">
            <h3 className="font-semibold text-lg">{product.title}</h3>

            {/* Description (formatted as list) */}
            {product.description && (
              <div className="space-y-2">
                {descriptionItems.length > 1 ? (
                  <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                    {(isExpanded ? descriptionItems : descriptionItems.slice(0, 2)).map(
                      (item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-neutral-400 mt-0.5">•</span>
                          <span className="flex-1">{item}</span>
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <p
                    className={`text-sm text-neutral-600 dark:text-neutral-400 ${
                      isExpanded ? '' : 'line-clamp-2'
                    }`}
                  >
                    {product.description}
                  </p>
                )}

                {/* Toggle button for long descriptions */}
                {hasLongDescription && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" />
                        Weniger anzeigen
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" />
                        Mehr anzeigen ({descriptionItems.length - 2} weitere)
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Product Metadata */}
            <div className="flex flex-wrap gap-4 text-sm">
              {product.price && (
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                  <DollarSign className="h-4 w-4" />
                  {product.price} {product.currency || 'EUR'}
                </div>
              )}

              {product.brand && (
                <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                  <Building2 className="h-4 w-4" />
                  {product.brand}
                </div>
              )}

              {product.category && (
                <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
                  <Tag className="h-4 w-4" />
                  {product.category}
                </div>
              )}
            </div>

            {/* Source Info */}
            <div className="text-xs text-neutral-400 space-y-1">
              <div>Quelle: {product.source.toUpperCase()}</div>
              <div className="truncate max-w-md" title={product.source_url}>
                {product.source_url}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
